import { useState, useEffect, useRef } from 'react'
import { Printer, Trophy, RefreshCw } from 'lucide-react'
import mapasService from '../../services/mapasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtDate = s => {
  if (!s) return ''
  try {
    // Trata como data local (sem conversão UTC)
    const [y, m, d] = s.split('-')
    return `${d}/${m}/${y}`
  } catch { return s }
}

// Períodos predefinidos
function getPeriodos() {
  const hoje = new Date()
  const ano = hoje.getFullYear()
  const mes = hoje.getMonth()

  const fmt = d => d.toISOString().split('T')[0]
  const inicio = (y, m) => fmt(new Date(y, m, 1))
  const fim = (y, m) => fmt(new Date(y, m + 1, 0))

  return [
    { label: 'Último mês', de: inicio(ano, mes - 1), ate: fim(ano, mes - 1) },
    { label: 'Últimos 3 meses', de: inicio(ano, mes - 3), ate: fmt(hoje) },
    { label: 'Últimos 6 meses', de: inicio(ano, mes - 6), ate: fmt(hoje) },
    { label: 'Último ano', de: inicio(ano - 1, mes), ate: fmt(hoje) },
    { label: 'Ano atual', de: inicio(ano, 0), ate: fmt(hoje) },
    { label: 'Ano anterior', de: inicio(ano - 1, 0), ate: fim(ano - 1, 11) },
    { label: 'Personalizado', de: '', ate: '' },
  ]
}

const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { borderBottom: '2px solid #1f2937', paddingBottom: 12, marginBottom: 18 },
  hdrT1: { fontSize: 18, fontWeight: 700 },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 12, color: '#6b7280', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 },
  th: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11 },
  thC: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11 },
  thR: { borderBottom: '2px solid #d1d5db', padding: '8px 8px', textAlign: 'right', fontWeight: 700, fontSize: 11 },
  td: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  tdR: { borderBottom: '1px solid #e5e7eb', padding: '7px 8px', textAlign: 'right', verticalAlign: 'middle', fontSize: 12 },
  totRow: { fontWeight: 700, borderTop: '2px solid #1f2937', background: '#f3f4f6' },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

export default function RankingClientes({ tipo, titulo, tituloRelatorio }) {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const periodos = getPeriodos()
  const [periodoIdx, setPeriodoIdx] = useState(4) // Ano atual
  const [dataInicio, setDataInicio] = useState(periodos[4].de)
  const [dataFim, setDataFim] = useState(periodos[4].ate)
  const [dados, setDados] = useState(null)
  const [loading, setLoading] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  const handlePeriodoChange = (idx) => {
    setPeriodoIdx(idx)
    if (idx < periodos.length - 1) {
      setDataInicio(periodos[idx].de)
      setDataFim(periodos[idx].ate)
    }
  }

  const fetchDados = () => {
    if (!orgId || !dataInicio || !dataFim) return
    setLoading(true)
    mapasService.getRankingClientes(orgId, tipo, dataInicio, dataFim)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    if (orgId && dataInicio && dataFim && periodoIdx < periodos.length - 1) fetchDados()
  }, [orgId, dataInicio, dataFim]) // eslint-disable-line

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getRankingClientesPdf(orgId, tipo, dataInicio, dataFim)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url)
      setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }
  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const totalClientes = dados?.clientes?.length || 0
  const totalLotes = dados?.clientes?.reduce((s, c) => s + c.lotes, 0) || 0
  const totalAnimais = dados?.clientes?.reduce((s, c) => s + c.qtd_animais, 0) || 0
  const totalValor = dados?.clientes?.reduce((s, c) => s + c.valor, 0) || 0

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Trophy className="w-5 h-5 text-amber-500" />
        <h1 className="text-lg font-semibold text-gray-800">{titulo}</h1>
      </div>

      {/* Seletor */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1 w-full sm:w-auto">
            <label className="text-xs text-gray-500 font-medium">Período</label>
            <div className="flex rounded-lg border border-gray-300 overflow-x-auto text-sm">
              {periodos.map((p, i) => (
                <button key={i} type="button" onClick={() => handlePeriodoChange(i)}
                  className={`px-3 py-2 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${periodoIdx === i ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {periodoIdx === periodos.length - 1 && (
            <>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">De</label>
                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">Até</label>
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              </div>
            </>
          )}

          <button onClick={fetchDados} disabled={loading || !dataInicio || !dataFim}
            className="px-4 py-2 h-9 bg-gray-800 hover:bg-gray-900 disabled:bg-gray-200 text-white rounded-lg text-sm font-medium transition-colors">
            Pesquisar
          </button>

          <div className="ml-auto">
            <button onClick={handleImprimir} disabled={!dados || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
          </div>
        </div>
      </div>

      {/* Preview */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">Selecione um período e clique em Pesquisar</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          <div style={S.page}>
            <div style={{ ...S.hdr, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ minWidth: 140 }}>
                {organizadorAtivo?.logo_url && <img src={organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`} alt="logo" style={{ maxHeight: 80, maxWidth: 180, objectFit: 'contain' }} />}
              </div>
              <div style={{ flex: 1, textAlign: 'center', padding: '0 16px' }}>
                <div style={S.hdrT1}>{tituloRelatorio}</div>
                <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
                <div style={S.hdrT3}>DATA {fmtDate(dataInicio)} À {fmtDate(dataFim)}</div>
              </div>
              <div style={{ minWidth: 140, textAlign: 'right' }}>
                <img src="/assets/rematei.png" alt="Rematei" style={{ maxHeight: 80, maxWidth: 180, objectFit: 'contain' }} />
              </div>
            </div>

            <table style={S.tbl}>
              <thead><tr>
                <th style={{ ...S.thC, width: '5%' }}>Pos.</th>
                <th style={{ ...S.thC, width: '5%' }}>ID</th>
                <th style={{ ...S.th, width: '25%' }}>Nome</th>
                <th style={{ ...S.th, width: '20%' }}>Fazenda</th>
                <th style={{ ...S.th, width: '13%' }}>Telefone</th>
                <th style={{ ...S.thC, width: '7%' }}>Lotes</th>
                <th style={{ ...S.thC, width: '10%' }}>Qtd. Animais</th>
                <th style={{ ...S.thR, width: '15%' }}>Valor</th>
              </tr></thead>
              <tbody>
                {dados.clientes.map((c, i) => (
                  <tr key={i}>
                    <td style={S.tdC}>{String(i + 1).padStart(3, '0')}</td>
                    <td style={S.tdC}>{c.codigo}</td>
                    <td style={S.td}><strong>{c.nome}</strong></td>
                    <td style={S.td}>{c.fazenda || '—'}</td>
                    <td style={S.td}>{c.telefone || '—'}</td>
                    <td style={S.tdC}>{c.lotes}</td>
                    <td style={S.tdC}>{c.qtd_animais}</td>
                    <td style={S.tdR}>{brl(c.valor)}</td>
                  </tr>
                ))}
                <tr style={S.totRow}>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}><strong>TOTAL: {totalClientes} clientes</strong></td>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                  <td style={{ ...S.td, borderTop: '2px solid #1f2937' }}></td>
                  <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalLotes}</strong></td>
                  <td style={{ ...S.tdC, borderTop: '2px solid #1f2937' }}><strong>{totalAnimais}</strong></td>
                  <td style={{ ...S.tdR, borderTop: '2px solid #1f2937' }}><strong>{brl(totalValor)}</strong></td>
                </tr>
              </tbody>
            </table>

            <div style={S.footer}>www.rematei.com.br</div>
          </div>
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">{titulo}</span>
              <div className="flex items-center gap-2">
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title={titulo} style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}
    </div>
  )
}
